import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowLeft, MapPin, TrendingUp, ShieldAlert, CheckCircle2, ArrowRight } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import AfricaInvestorModal from '../components/AfricaInvestorModal';
import AfricaSituationModal from '../components/AfricaSituationModal';
import { africaPlatform } from '../data/mock';

const AfricaPage = () => {
  const [investorOpen, setInvestorOpen] = useState(false);
  const [situationOpen, setSituationOpen] = useState(false);
  const location = useLocation();

  // Open the relevant modal if URL hash is #investor or #situation
  useEffect(() => {
    window.scrollTo(0, 0);
    if (location.hash === '#investor') setInvestorOpen(true);
    if (location.hash === '#situation') setSituationOpen(true);
  }, [location.hash]);

  return (
    <div className="App font-sans bg-white">
      <Header onPartnerClick={() => setInvestorOpen(true)} />

      <main>
        {/* HERO */}
        <section className="relative pt-32 pb-20 px-6 bg-[#0d1f3a] overflow-hidden" data-testid="africa-page-hero">
          <div className="absolute inset-0 opacity-[0.05]" style={{
            backgroundImage: 'linear-gradient(#2f9e8e 1px, transparent 1px), linear-gradient(90deg, #2f9e8e 1px, transparent 1px)',
            backgroundSize: '64px 64px'
          }} />
          <div className="relative max-w-7xl mx-auto text-white">
            <Link to="/" className="inline-flex items-center gap-2 text-white/60 hover:text-white text-sm mb-8" data-testid="africa-page-back-link">
              <ArrowLeft className="w-4 h-4" /> Back to Berkshire Investment Group
            </Link>

            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#2f9e8e]/40 bg-[#2f9e8e]/10 text-[#7fd6c9] text-xs font-semibold tracking-widest uppercase mb-6">
              <MapPin className="w-3.5 h-3.5" /> African Distressed & Special Situations Capital Platform
            </div>

            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold leading-tight max-w-4xl mb-6">
              {africaPlatform.brandName}
            </h1>

            <p className="text-lg md:text-xl text-white/80 max-w-3xl leading-relaxed mb-10">
              {africaPlatform.tagline}
            </p>

            <div className="flex flex-wrap gap-4">
              <button onClick={() => setInvestorOpen(true)}
                className="bg-[#2f9e8e] hover:bg-[#268578] text-white px-7 py-3.5 rounded-lg font-medium transition-all hover:shadow-xl flex items-center gap-2"
                data-testid="africa-page-investor-cta">
                <TrendingUp className="w-5 h-5" /> Investor Inquiry
              </button>
              <button onClick={() => setSituationOpen(true)}
                className="bg-[#a83232] hover:bg-[#8b2828] text-white px-7 py-3.5 rounded-lg font-medium transition-all hover:shadow-xl flex items-center gap-2"
                data-testid="africa-page-situation-cta">
                <ShieldAlert className="w-5 h-5" /> Submit a Situation
              </button>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-16">
              {africaPlatform.highlightStats.map((s) => (
                <div key={s.label} className="border-l-2 border-[#2f9e8e] pl-4">
                  <div className="text-3xl font-bold text-white">{s.value}</div>
                  <div className="text-sm text-white/80 mt-1">{s.label}</div>
                  <div className="text-xs text-white/50">{s.sublabel}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* THESIS */}
        <section className="py-20 px-6 bg-white">
          <div className="max-w-5xl mx-auto">
            <div className="text-xs font-semibold tracking-widest text-[#2f9e8e] uppercase mb-3">Investment Thesis</div>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1a365d] mb-6 leading-tight">
              Africa is capital constrained — not opportunity constrained.
            </h2>
            <p className="text-gray-700 text-lg leading-relaxed mb-4">
              {africaPlatform.thesis}
            </p>
            <p className="text-gray-700 text-lg leading-relaxed">
              Most businesses fail because of duration mismatch, expensive USD debt, poor treasury management, working-capital starvation, FX shortages, or refinancing walls — <strong className="text-[#1a365d]">not</strong> because the underlying assets are worthless. That gap creates forced sellers, distressed valuations, and strategic acquisition opportunities.
            </p>
          </div>
        </section>

        {/* PILLARS */}
        <section className="py-20 px-6 bg-[#f6f9fb]">
          <div className="max-w-7xl mx-auto">
            <div className="max-w-3xl mb-12">
              <div className="text-xs font-semibold tracking-widest text-[#2f9e8e] uppercase mb-3">Business Model</div>
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1a365d] leading-tight">Four pillars. One integrated platform.</h2>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              {africaPlatform.pillars.map((p) => (
                <div key={p.title} className="bg-white rounded-xl p-7 border border-gray-100 hover:border-[#2f9e8e]/30 transition-all hover:shadow-lg">
                  <div className="text-xs font-bold text-[#2f9e8e] tracking-widest uppercase mb-3">{p.tag}</div>
                  <h3 className="font-serif text-2xl font-bold text-[#1a365d] mb-3">{p.title}</h3>
                  <p className="text-gray-600 leading-relaxed mb-5">{p.description}</p>
                  <ul className="space-y-2">
                    {p.points.map((pt) => (
                      <li key={pt} className="flex items-start gap-2 text-sm text-gray-700">
                        <CheckCircle2 className="w-4 h-4 text-[#2f9e8e] flex-shrink-0 mt-0.5" />
                        {pt}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* GEOGRAPHIES */}
        <section className="py-20 px-6 bg-white">
          <div className="max-w-7xl mx-auto">
            <div className="max-w-3xl mb-12">
              <div className="text-xs font-semibold tracking-widest text-[#2f9e8e] uppercase mb-3">Geographic Focus</div>
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1a365d] leading-tight">Nine priority markets across two tiers.</h2>
            </div>

            <div className="grid lg:grid-cols-2 gap-8">
              <div>
                <div className="flex items-baseline gap-3 mb-5">
                  <span className="inline-block px-3 py-1 rounded-full bg-[#1a365d] text-white text-xs font-bold tracking-widest uppercase">Tier 1</span>
                  <span className="text-sm text-gray-500">Highest opportunity + institutional depth</span>
                </div>
                <div className="space-y-2">
                  {africaPlatform.geographies.tier1.map((m) => (
                    <div key={m.name} className="flex items-center gap-4 p-4 border border-gray-100 rounded-lg hover:bg-[#f6f9fb] transition-colors">
                      <div className="font-bold text-[#1a365d] w-32 flex-shrink-0">{m.name}</div>
                      <div className="text-sm text-gray-600">{m.note}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <div className="flex items-baseline gap-3 mb-5">
                  <span className="inline-block px-3 py-1 rounded-full bg-[#2f9e8e] text-white text-xs font-bold tracking-widest uppercase">Tier 2</span>
                  <span className="text-sm text-gray-500">Frontier opportunities, higher return</span>
                </div>
                <div className="space-y-2">
                  {africaPlatform.geographies.tier2.map((m) => (
                    <div key={m.name} className="flex items-center gap-4 p-4 border border-gray-100 rounded-lg hover:bg-[#f6f9fb] transition-colors">
                      <div className="font-bold text-[#1a365d] w-32 flex-shrink-0">{m.name}</div>
                      <div className="text-sm text-gray-600">{m.note}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SECTORS */}
        <section className="py-20 px-6 bg-[#f6f9fb]">
          <div className="max-w-7xl mx-auto">
            <div className="max-w-3xl mb-12">
              <div className="text-xs font-semibold tracking-widest text-[#2f9e8e] uppercase mb-3">Priority Sectors</div>
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1a365d] leading-tight">Where the asymmetry is largest.</h2>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
              {africaPlatform.sectors.map((s, idx) => (
                <div key={s.title} className="bg-white rounded-xl p-6 border border-gray-100 hover:shadow-md transition-all">
                  <div className="text-3xl font-serif font-bold text-[#2f9e8e]/30 mb-2">{String(idx + 1).padStart(2, '0')}</div>
                  <h3 className="font-serif text-xl font-bold text-[#1a365d] mb-3">{s.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{s.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* WHY WE WIN */}
        <section className="py-20 px-6 bg-white">
          <div className="max-w-7xl mx-auto">
            <div className="max-w-3xl mb-12">
              <div className="text-xs font-semibold tracking-widest text-[#2f9e8e] uppercase mb-3">Competitive Advantage</div>
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1a365d] leading-tight">Why this platform wins where others can't.</h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {africaPlatform.whyWeWin.map((w) => (
                <div key={w.title} className="border-t-2 border-[#2f9e8e] pt-5">
                  <h3 className="font-serif text-lg font-bold text-[#1a365d] mb-2">{w.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{w.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* DUAL CTA */}
        <section className="py-20 px-6 bg-[#0d1f3a]" id="cta">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-white leading-tight mb-4">
                Two ways to engage.
              </h2>
              <p className="text-white/70 max-w-2xl mx-auto">
                Whether you are deploying capital or facing a situation, the conversation starts here.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
              <div id="investor" className="bg-white/[0.04] border border-white/10 rounded-2xl p-8 hover:border-[#2f9e8e]/50 transition-all">
                <div className="w-12 h-12 rounded-lg bg-[#2f9e8e]/20 flex items-center justify-center mb-5">
                  <TrendingUp className="w-6 h-6 text-[#7fd6c9]" />
                </div>
                <div className="text-xs uppercase tracking-widest text-[#7fd6c9] font-semibold mb-2">For Capital Partners</div>
                <h3 className="font-serif text-2xl font-bold text-white mb-3">{africaPlatform.ctas.investor.title}</h3>
                <p className="text-white/70 mb-6 leading-relaxed">{africaPlatform.ctas.investor.blurb}</p>
                <button onClick={() => setInvestorOpen(true)}
                  className="w-full bg-[#2f9e8e] hover:bg-[#268578] text-white px-6 py-3.5 rounded-lg font-medium transition-all flex items-center justify-center gap-2"
                  data-testid="africa-page-investor-cta-bottom">
                  {africaPlatform.ctas.investor.button} <ArrowRight className="w-4 h-4" />
                </button>
              </div>

              <div id="situation" className="bg-white/[0.04] border border-white/10 rounded-2xl p-8 hover:border-[#a83232]/60 transition-all">
                <div className="w-12 h-12 rounded-lg bg-[#a83232]/20 flex items-center justify-center mb-5">
                  <ShieldAlert className="w-6 h-6 text-[#f0a8a8]" />
                </div>
                <div className="text-xs uppercase tracking-widest text-[#f0a8a8] font-semibold mb-2">For Distressed Situations</div>
                <h3 className="font-serif text-2xl font-bold text-white mb-3">{africaPlatform.ctas.situation.title}</h3>
                <p className="text-white/70 mb-6 leading-relaxed">{africaPlatform.ctas.situation.blurb}</p>
                <button onClick={() => setSituationOpen(true)}
                  className="w-full bg-[#a83232] hover:bg-[#8b2828] text-white px-6 py-3.5 rounded-lg font-medium transition-all flex items-center justify-center gap-2"
                  data-testid="africa-page-situation-cta-bottom">
                  {africaPlatform.ctas.situation.button} <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />

      <AfricaInvestorModal isOpen={investorOpen} onClose={() => setInvestorOpen(false)} />
      <AfricaSituationModal isOpen={situationOpen} onClose={() => setSituationOpen(false)} />
    </div>
  );
};

export default AfricaPage;
